import React, { useCallback, useMemo, useState } from "react";
import { App as AntApp, Button, Card, Flex, Tabs } from "antd";
import { LinkOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { BasicModal } from "../../../../components/Shared";
import {
  MultiStepForm,
  EducamakerUpsellModal,
} from "../../../../components/Admin/VirtualScenes";
import { PageHeader, BrandThemeProvider } from "../../../../ui";
import { useThemeRuntime } from "../../../../ui/theme/ThemeRuntimeProvider";
import {
  NEW_EDUCAMAKER,
  EDUCAMAKER_UPSELL_GATE,
} from "../../../../ui/featureFlags";
import { useAuth } from "../../../../hooks/useAuth";
import {
  EDUCAMAKER_URL,
  getEffectivePlanKey,
  hasEducamakerAccess,
  trackEducamakerUpsell,
} from "../../../../Utils/educamakerAccess";

import { SceneList } from "../../../../components/Admin/VirtualScenes/SceneList";

import paso1 from "../../../../assets/illustrations/guia-vscenes/paso1.png";
import paso2 from "../../../../assets/illustrations/guia-vscenes/paso2.png";
import paso3 from "../../../../assets/illustrations/guia-vscenes/paso3.png";

import styles from "./VirtualScenes.module.scss";
import "./VirtualScenes.scss";

export function VirtualScenes() {
  const { t } = useTranslation("scenes");
  const { accessToken, user } = useAuth();
  // Shape normal: { code, message, user } (getMe); tras login Google puede
  // llegar el doc crudo un instante — cubrimos ambos.
  const userDoc = user?.user ?? user;
  const [showModal, setShowModal] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [reload, setReload] = useState(false);

  const onOpenCloseModal = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  const onReload = useCallback(() => {
    setReload((prev) => !prev);
  }, []);

  // Overrides del editor de estilos (guia rapida por pagina).
  const { resolvedOverrides } = useThemeRuntime();
  const ov = (key, fallback) => {
    const v = resolvedOverrides[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };
  const guideTitleOv = ov("text.vscenes.guide.title", undefined);

  const steps = useMemo(
    () => {
      const imgs = [paso1, paso2, paso3];
      return [1, 2, 3].map((n) => ({
        title: ov(`text.vscenes.guide.step${n}.title`, t(`page.step${n}Title`)),
        description: ov(
          `text.vscenes.guide.step${n}.desc`,
          t(`page.step${n}Desc`),
        ),
        illustration: ov(`image.vscenes.guide.step${n}`, imgs[n - 1]),
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, resolvedOverrides],
  );

  // Gate EducaMaker por plan (P1, plan maestro EDUCAMAKER_ACCESO_PLAN_MAESTRO):
  // los planes sin EducaMaker ven el popup de upsell; el resto entra directo.
  const handleOpenEducamaker = useCallback(() => {
    if (!EDUCAMAKER_UPSELL_GATE || hasEducamakerAccess(accessToken, userDoc)) {
      window.open(EDUCAMAKER_URL, "_blank", "noopener,noreferrer");
      return;
    }
    trackEducamakerUpsell("educamaker_upsell_shown", {
      planKey: getEffectivePlanKey(accessToken, userDoc),
    });
    setShowUpsell(true);
  }, [accessToken, userDoc]);

  const handleCloseUpsell = useCallback(() => {
    setShowUpsell(false);
  }, []);

  const tabItems = useMemo(
    () => [
      {
        key: "created",
        label: t("page.createdTab"),
        children: <SceneList reload={reload} onReload={onReload} />,
      },
    ],
    [reload, onReload, t],
  );

  const content = (
    <AntApp>
      <div className={styles.page}>
        <PageHeader
          title={t("page.title")}
          description={t("page.description")}
          steps={steps}
          guideTitle={guideTitleOv}
          extra={
            <Flex gap={10} wrap className={styles.actions}>
              <Button
                size="large"
                icon={<LinkOutlined />}
                className={styles.secondaryAction}
                onClick={handleOpenEducamaker}
              >
                Educamaker
              </Button>

              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                className={NEW_EDUCAMAKER ? undefined : styles.primaryAction}
                onClick={onOpenCloseModal}
              >
                {t("page.addSceneButton")}
              </Button>
            </Flex>
          }
        />

        <Card className={styles.contentCard} styles={{ body: { padding: 12 } }}>
          <Tabs items={tabItems} className={styles.tabs} />
        </Card>
      </div>

      <BasicModal
        show={showModal}
        close={onOpenCloseModal}
        title={t("page.createModalTitle")}
      >
        <MultiStepForm onClose={onOpenCloseModal} onReload={onReload} />
      </BasicModal>

      <EducamakerUpsellModal
        open={showUpsell}
        onClose={handleCloseUpsell}
        planKey={getEffectivePlanKey(accessToken, userDoc)}
      />
    </AntApp>
  );

  if (NEW_EDUCAMAKER) {
    return <BrandThemeProvider>{content}</BrandThemeProvider>;
  }

  return content;
}
